import { useMutation } from '@tanstack/react-query'
import { Loader2, ShieldAlert } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { useForm } from 'react-hook-form'
import { StaffStepUpFields } from '@/features/auth/components/staff-step-up-fields'
import useAuth from '@/features/auth/hooks/use-auth'
import { useStaffStepUp } from '@/features/auth/hooks/use-staff-step-up'
import { Button, type ButtonProps } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/shared/ui/dialog'
import { extractApiError, handleMutationError } from '@/shared/utils/handle-api-error'

type StepUpForm = {
  password: string
  verificationCode: string
}

type StaffStepUpDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: ReactNode
  description: ReactNode
  confirmLabel: string
  confirmVariant?: ButtonProps['variant']
  /** Contenido opcional (p. ej. opciones de la operación) por encima del step-up. */
  children?: ReactNode
  /**
   * Ejecuta la operación sensible. Recibe el access token con el que correrla: el de la
   * sesión mientras la ventana sudo siga vigente, o uno recién acuñado tras el step-up.
   * Si lanza, el diálogo mapea los errores de credenciales a los campos y deja el resto
   * para el toast; sólo se cierra cuando la operación termina bien.
   */
  onConfirm: (accessToken: string) => Promise<void>
}

/**
 * Diálogo de confirmación para operaciones sensibles con step-up PEREZOSO ("sudo
 * mode", como GitHub): primero intenta la operación con la sesión actual y sólo pide
 * volver a validar la identidad cuando el backend responde `MFA_STEP_UP_REQUIRED`
 * (la verificación MFA de la sesión venció, ver `mfaMaxAgeSeconds`). Así se valida una
 * vez y el resto de las acciones sensibles fluyen durante esa ventana. Es agnóstico
 * del dominio — la operación se inyecta por prop. Pareja de [[staff-step-up-fields]].
 */
export function StaffStepUpDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  confirmVariant,
  children,
  onConfirm
}: StaffStepUpDialogProps) {
  const { auth } = useAuth()
  const stepUp = useStaffStepUp()
  const [needsStepUp, setNeedsStepUp] = useState(false)
  const { control, handleSubmit, reset, setError } = useForm<StepUpForm>({
    defaultValues: { password: '', verificationCode: '' }
  })

  const close = () => {
    onOpenChange(false)
    reset()
    stepUp.reset()
    setNeedsStepUp(false)
  }

  const mutation = useMutation({
    mutationFn: async (values: StepUpForm) => {
      if (!needsStepUp) {
        if (!auth?.v2AccessToken) throw new Error('Missing staff session')
        try {
          await onConfirm(auth.v2AccessToken)
          return { status: 'confirmed' as const }
        } catch (error) {
          if (extractApiError(error)?.errorCode !== 'MFA_STEP_UP_REQUIRED') throw error
          setNeedsStepUp(true)
          return { status: 'awaiting_step_up' as const }
        }
      }

      const accessToken = await stepUp.acquireAccessToken({
        password: values.password,
        verificationCode: values.verificationCode
      })
      if (!accessToken) return { status: 'awaiting_second_factor' as const }
      await onConfirm(accessToken)
      return { status: 'confirmed' as const }
    },
    onSuccess: (result) => {
      if (result.status !== 'confirmed') return
      close()
    },
    onError: (error) => {
      const code = extractApiError(error)?.errorCode
      if (code === 'MFA_CODE_INVALID') {
        setError('verificationCode', { message: 'El código de autenticación no es válido.' })
        return
      }
      if (code === 'PASSWORD_NOT_VALID' || code === 'USER_OR_PASSWORD_INVALID') {
        setError('password', { message: 'La contraseña es incorrecta.' })
        return
      }
      handleMutationError(error)
    }
  })

  const blockedByEnrollment = needsStepUp && !stepUp.isMfaEnrolled

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) close()
        else onOpenChange(true)
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {children}

        {blockedByEnrollment ? (
          <div className="flex items-start gap-3 rounded-lg border border-amber-300/60 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
            <ShieldAlert className="mt-0.5 size-5 shrink-0" />
            <p>
              Para confirmar esta operación primero activa la verificación en dos pasos desde la
              pestaña <span className="font-medium">Seguridad</span> de tu cuenta.
            </p>
          </div>
        ) : (
          <form
            id="staff-step-up-dialog-form"
            className="space-y-4"
            onSubmit={handleSubmit((values) => mutation.mutate(values))}
          >
            {needsStepUp && (
              <>
                <p className="text-sm text-muted-foreground">
                  Por seguridad, vuelve a confirmar tu identidad para continuar.
                </p>
                <StaffStepUpFields
                  control={control}
                  passwordName="password"
                  codeName="verificationCode"
                  needsSecondFactor={stepUp.needsSecondFactor}
                />
              </>
            )}
          </form>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={close}>
            Cancelar
          </Button>
          {!blockedByEnrollment && (
            <Button
              form="staff-step-up-dialog-form"
              type="submit"
              variant={confirmVariant}
              disabled={mutation.isPending}
            >
              {mutation.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
              {needsStepUp ? `Verificar y ${confirmLabel.toLowerCase()}` : confirmLabel}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
